import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg?react';
import ApplePayMark from '../../assets/icons/applepay.svg?react';
import { openModal, closeModal } from '../../redux/slices/modals';
import { TitleSmall } from '../Texts';
import { formatUSDEnsure2Digits } from '../../common/currency';

const PaymentSelectorContainer = styled.div`
  display: flex;
  gap: 12px;
  cursor: pointer;
  justify-content: flex-start;
  align-items: center;
  width: fit-content;
  ${({ $hasBorder, theme }) =>
    $hasBorder
      ? `
    padding: 16px;
    border: 1px solid ${theme.border.primary};
    border-radius: 8px;
    background-color: ${theme.background.primary};
    transition: background-color 0.2s;
    width: 100%;

    &:hover {
      background-color: ${theme.background.secondary || '#f5f5f5'};
    }
  `
      : ''}
`;

const TokenImage = styled.img`
  width: 24px;
  height: 24px;
`;

const PaymentTokenInfoText = styled(TitleSmall)`
  margin: 0;
`;

const PaymentMethodSelector = ({
  selectedPaymentMethodType,
  selectedPaymentToken,
  onPaymentMethodSelected,
  amount,
  targetToken = null,
  amountSpecifiedInUsd = false,
  userSelectedPaymentTokenUsdcBalance = 0,
  userSelectedTokenBalance = null,
  onAnalyticsEvent = null,
  tokensWithUSDBalance = null,
  tokensWithEnoughBalance = null,
  canOpenModal = true,
  style = {},
  hasBorder = false,
}) => {
  const dispatch = useDispatch();

  const handleClick = () => {
    // Check if modal can be opened
    if (!canOpenModal) {
      return;
    }

    // For gift flow, check if we have token data
    // Only apply this check if both props are explicitly provided (not null/undefined)
    if (
      tokensWithUSDBalance !== undefined &&
      tokensWithUSDBalance !== null &&
      tokensWithEnoughBalance !== undefined &&
      tokensWithEnoughBalance !== null
    ) {
      if (!tokensWithUSDBalance && tokensWithEnoughBalance === null) {
        return;
      }
    }

    // Trigger analytics event if provided
    if (onAnalyticsEvent) {
      onAnalyticsEvent();
    }

    // Open payment suggestion modal
    dispatch(
      openModal({
        name: 'paymentSuggestion',
        props: {
          amount: amount,
          targetToken: targetToken,
          onCancel: () => {
            dispatch(closeModal());
          },
          onPaymentMethodSelected: (paymentData) => {
            if (onPaymentMethodSelected) {
              onPaymentMethodSelected(paymentData);
            }
            dispatch(closeModal());
          },
        },
      }),
    );
  };

  return (
    <PaymentSelectorContainer $hasBorder={hasBorder} style={style} onClick={handleClick}>
      {selectedPaymentMethodType === 'applePay' ? (
        <>
          <ApplePayMark
            style={{
              width: 40,
              height: 'auto',
            }}
          />
          <PaymentTokenInfoText $variant="text-title">Apple Pay</PaymentTokenInfoText>
          <ChevronRightIcon width={12} height={12} />
        </>
      ) : selectedPaymentMethodType === 'token' && selectedPaymentToken ? (
        <>
          <TokenImage src={selectedPaymentToken?.logoURI} />
          <PaymentTokenInfoText $variant="text-title">
            {selectedPaymentToken?.symbol}
            {userSelectedTokenBalance !== null ||
            userSelectedPaymentTokenUsdcBalance > 0
              ? ` (${
                  amountSpecifiedInUsd
                    ? formatUSDEnsure2Digits(userSelectedPaymentTokenUsdcBalance)
                    : userSelectedTokenBalance
                })`
              : ''}
          </PaymentTokenInfoText>
          <ChevronRightIcon width={12} height={12} />
        </>
      ) : (
        <>
          <PaymentTokenInfoText $variant="text-title">
            Select payment method
          </PaymentTokenInfoText>
          <ChevronRightIcon width={12} height={12} />
        </>
      )}
    </PaymentSelectorContainer>
  );
};

export default PaymentMethodSelector;
